import React, { useEffect, useState, useRef } from 'react'
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import fileUtils from '../../../global/utils/fileUtils.js'
import { getFileIcon } from '../utils/fileIconUtils.jsx'
import DeleteIcon from '@mui/icons-material/Delete'
import { formatDate } from '../utils/documentUtils.js'

const DocumentAccordion = ({
  doc,
  dragOverDocId,
  setDragOverDocId,
  onDragFiles,
  onDeleteFile,
}) => {
  const isOtherAccordion = doc.id === 'unmatched' || doc.isOther
  const [expanded, setExpanded] = useState(false)
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    if (doc.files?.length > 0) {
      setExpanded(true)
    }
  }, [doc.files?.length])

  const handleDragOver = e => {
    e.preventDefault()
    setDragOverDocId(doc.id)
  }

  const handleDragLeave = e => {
    // Only clear if we're leaving the accordion itself, not a child
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverDocId(null)
    }
  }

  const handleDrop = e => {
    e.preventDefault()
    setDragOverDocId(null)
    const fileId = e.dataTransfer.getData('fileId')
    const fromDocId = e.dataTransfer.getData('fromDocId')
    const toDocId = doc.id
    onDragFiles(fileId, fromDocId, toDocId)
  }

  const handleDragStart = (e, file) => {
    e.dataTransfer.setData('fileId', file._id)
    e.dataTransfer.setData('fromDocId', doc.id)
  }

  return (
    <Accordion
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      sx={{
        border: dragOverDocId === doc.id ? '2px dashed' : '1px solid',
        borderColor: dragOverDocId === doc.id ? 'primary.main' : 'divider',
        backgroundColor:
          dragOverDocId === doc.id ? 'action.hover' : 'transparent',
        transition: 'all 0.2s ease',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {isOtherAccordion ? (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            padding={'7px'}
          >
            <Typography fontWeight="normal">
              {doc.name || doc.custom_name}
            </Typography>
            <Typography
              component="span"
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 'normal',
              }}
            >
              ({doc.files?.length || 0} file{doc.files?.length !== 1 ? 's' : ''}
              )
            </Typography>
          </Box>
        ) : (
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  doc.settings?.number_of_files != null
                    ? doc.files?.length === doc.settings.number_of_files
                    : doc.files?.length > 0
                }
                readOnly
                onClick={e => e.stopPropagation()}
              />
            }
            label={
              <Box
                display="flex"
                alignItems="center"
                gap={1}
              >
                <span>{doc.name || doc.custom_name}</span>
                {doc.settings?.number_of_files && (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 'normal',
                    }}
                  >
                    ({doc.files?.length || 0}/{doc.settings.number_of_files})
                  </Typography>
                )}
              </Box>
            }
          />
        )}
      </AccordionSummary>
      <AccordionDetails>
        {!isOtherAccordion && (doc.range_from_date || doc.range_to_date) && (
          <Typography
            variant="body2"
            sx={{ mb: 2 }}
          >
            <strong>From:</strong> {formatDate(doc.range_from_date)} |{' '}
            <strong>To:</strong> {formatDate(doc.range_to_date)}
          </Typography>
        )}

        {doc.files?.length > 0 ? (
          doc.files.map(file => (
            <Box
              key={file._id}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
              padding={1}
              sx={{
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 1,
                cursor: 'grab',
                bgcolor: 'background.paper',
                '&:hover': { bgcolor: 'action.hover' },
              }}
              draggable
              onDragStart={e => handleDragStart(e, file)}
            >
              <Box
                display="flex"
                alignItems="center"
                flexGrow={1}
                overflow="hidden"
                mr={1}
              >
                {getFileIcon(file.fileExtension)}
                <Typography
                  variant="body2"
                  ml={1}
                  noWrap
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {file.filename}
                </Typography>
                <Typography
                  variant="body2"
                  ml={1}
                  sx={{ flexShrink: 0, color: 'text.secondary' }}
                >
                  ({fileUtils.formatFileSize(file.fileSize)})
                </Typography>
              </Box>

              <IconButton
                size="small"
                color="error"
                onClick={() => onDeleteFile(file._id, doc.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            No files uploaded yet.
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  )
}

export default DocumentAccordion
